<template>
  <div class="max-w-4xl mx-auto p-4 lg:p-6 bg-white shadow-md border border-iron rounded">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-secondary">Gerenciar Depoimentos</h1>
        <p class="text-sm text-gray-500">Exiba o que seus alunos dizem (Máximo: 15)</p>
      </div>
      <Button 
        label="Adicionar Novo" 
        icon="pi pi-plus" 
        @click="addTestimonial" 
        :disabled="testimonials.length >= 15"
        class="p-button-outlined p-button-sm"
      />
    </div>

    <!-- Empty State -->
    <div v-if="testimonials.length === 0" class="text-center py-10 border-2 border-dashed border-iron rounded-lg text-gray-400">
      Nenhum depoimento adicionado ainda.
    </div>

    <!-- Listagem de Depoimentos -->
    <div class="flex flex-col gap-4">
      <div 
        v-for="(item, index) in testimonials" 
        :key="index"
        class="p-4 border border-iron rounded-lg bg-gray-50 transition-all hover:shadow-sm relative group"
      >
        <!-- Header do Card: Ordem e Ações -->
        <div class="flex justify-between items-center mb-4 border-b pb-2 border-iron">
          <span class="font-bold text-sm text-gray-600">Depoimento #{{ index + 1 }}</span>
          <div class="flex gap-1">
            <Button 
              icon="pi pi-arrow-up" 
              @click="move(index, -1)" 
              :disabled="index === 0"
              class="p-button-text p-button-sm p-button-secondary" 
            />
            <Button 
              icon="pi pi-arrow-down" 
              @click="move(index, 1)" 
              :disabled="index === testimonials.length - 1"
              class="p-button-text p-button-sm p-button-secondary" 
            />
            <Button 
              icon="pi pi-trash" 
              @click="removeTestimonial(index)" 
              class="p-button-text p-button-sm p-button-danger" 
            />
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div class="md:col-span-3 flex flex-col items-center gap-2">
            <div class="w-20 h-20 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center border border-iron">
              <img v-if="item.photo" :src="item.className ? item.getPhoto() : item.photo" class="w-full h-full object-cover" />
              <i v-else class="pi pi-user text-3xl text-gray-400"></i>
            </div>
            <FileUpload 
              mode="basic" 
              accept="image/*" 
              :auto="true"
              :maxFileSize="1000000" 
              @select="(e) => onPhotoUpload(e, index)"
              chooseLabel="Foto" 
              class="p-button-xs p-button-text"
              :invalidFileTypeMessage="translate('Invalid file type')"
              :invalidFileSizeMessage="translate('invalid file size, file size should be smaller than 1MB', 'ucfirst')"
            />
          </div>

          <!-- Dados do Depoimento -->
          <div class="md:col-span-9 flex flex-col gap-3">
            <InputText 
              v-model="item.title" 
              placeholder="Nome do Aluno (Ex: João Silva)" 
              class="w-full p-inputtext-sm" 
            />
            <InputText 
              v-model="item.occupation" 
              placeholder="Ocupação do aluno (Ex: Advogado)" 
              class="w-full p-inputtext-sm" 
            />
            <Textarea 
              v-model="item.content" 
              placeholder="O que essa pessoa disse?" 
              rows="3" 
              class="w-full p-inputtext-sm" 
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Ações de Salvamento -->
    <div class="mt-8 pt-6 border-t flex justify-end gap-3 border-iron">
      <small class="self-center text-gray-400 mr-auto">
        {{ testimonials.length }}/15 depoimentos utilizados
      </small>
      <Button 
        label="Salvar Alterações" 
        icon="pi pi-save" 
        @click="save" 
        :loading="loading"
        class="bg-purple-suit-forced text-white px-6"
      />
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import FileUpload from 'primevue/fileupload';
import { useToast } from 'primevue/usetoast';
import { showToast } from '@/composables/toast';
import { translate } from '@/composables/translate';
import TestimonyService from '@/services/TestimonyService';

const toast = useToast();
const loading = ref(false);
const testimonials = ref([]);

onMounted(() => {
  getTestimonies();
});

async function getTestimonies() {
  loading.value = true;
  testimonials.value = await TestimonyService.get();
  loading.value = false;
}

const addTestimonial = () => {
  if (testimonials.value.length < 15) {
    testimonials.value.push({
      title: '',
      occupation: '',
      content: '',
      photo: null,
      testimony_order: testimonials.value.length
    });
  }
};

const removeTestimonial = (index) => {
  testimonials.value.splice(index, 1);
};

const move = (index, direction) => {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= testimonials.value.length) return;
  
  const element = testimonials.value.splice(index, 1)[0];
  testimonials.value.splice(targetIndex, 0, element);
};

const hasChangedPhoto = ref(false);
const onPhotoUpload = (event, index) => {
  try {
    const file = event.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      testimonials.value[index].photo = e.target.result;
      hasChangedPhoto.value = true;
    };
    reader.readAsDataURL(file);
    
  } catch (error) {
    showToast(toast, translate('invalid file'), 'error');
  }
};

const save = async () => {
  const isInvalid = testimonials.value.some(t => !t.title || !t.content);
  if (isInvalid) {
    showToast(toast, translate('all the names and content must be filled', 'ucfirst'), 'error');
    return;
  }

  loading.value = true;

  let index = 1;
  let data = [];
  for (let testimony of testimonials.value) {
    let testimonyData = testimony;
    testimonyData.testimony_order = index;
    index++;

    if (!hasChangedPhoto.value) {
      testimonyData.photo = null;
    }

    if (testimonyData.photo && !testimonyData.photo.includes('data:image/jpeg;base64,')) {
      testimonyData.photo = `data:image/jpeg;base64,${testimonyData.photo}`;
    }

    data.push(testimonyData);
  }

  const response = await TestimonyService.save(data);

  if (!response.success()) {
    showToast(toast, response.getMessage(), 'error');
  }

  testimonials.value = response.getContent();
  showToast(toast, translate('testimonies saved with success'), 'ucfirst');

  loading.value = false;
};
</script>

<style scoped>
.p-button-xs {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
}
</style>